import React, { useEffect, useState } from "react";
import DataTableBase from "../../../Components/Common/DataTableBase/DataTableBase";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { useKitchenTabletsStore } from "../../../stores/Assets/kitchenTablets";
import { useKitchenStore } from "../../../stores/Assets/kitchen";
import { useSiteStore } from "../../../stores/Assets/site";
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  UncontrolledTooltip,
  Toast,
  ToastBody,
} from "reactstrap";
import { Pagination } from "../../../Components/Common/DataTableBase/Pagination";
import { canEdit } from "../../../helpers/permissions";
import SearchBar from "../../../Components/Common/SearchBar";
import Skeleton from "../../../Components/Common/Skeleton";
import { useConfirmDialogStore } from "../../../stores/Modal/ConfirmDialogStore";
import moment from "moment";

const KitchenTablets = () => {
  const { t } = useTranslation();
  const { kitchenId } = useParams();
  const navigate = useNavigate();
  
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 20;
  const [searchTerm, setSearchTerm] = useState("");
  const [kitchen, setKitchen] = useState(null);
  const [canDeleteTablets, setCanDeleteTablets] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedTablet, setSelectedTablet] = useState(null);
  const [showCopyToast, setShowCopyToast] = useState(false);
  const [sites, setSites] = useState([]);
  const [kitchens, setKitchens] = useState([]);
  const [selectedSiteId, setSelectedSiteId] = useState("all");
  const [selectedKitchenId, setSelectedKitchenId] = useState("all");
  const [isLoadingFilters, setIsLoadingFilters] = useState(true);
  const [minVersion, setMinVersion] = useState("");
  const [versionComparisonType, setVersionComparisonType] = useState("gte");
  const [filteredTablets, setFilteredTablets] = useState([]);

  document.title = t("Clickorder Admin - Kitchen Tablets");

  const {
    getKitchenTablets,
    getAllTablets,
    getTabletsBySite,
    isLoading,
    tablets,
    pagination,
    unlinkTablet,
  } = useKitchenTabletsStore((state) => state);

  const { getKitchensBySite } = useKitchenStore((state) => state);
  const { getSites } = useSiteStore((state) => state);

  // Check permissions
  useEffect(() => {
    const checkPermissions = async () => {
      const hasEdit = await canEdit("kitchen");
      setCanDeleteTablets(hasEdit);
    };
    checkPermissions();
  }, []);

  // Fetch sites and kitchens on mount
  useEffect(() => {
    const fetchFilters = async () => {
      setIsLoadingFilters(true);
      try {
        const siteList = await getSites();
        setSites(siteList || []);

        // If kitchenId is in URL params, set it; otherwise use "all"
        if (kitchenId) {
          setSelectedKitchenId(Number(kitchenId));
          setSelectedSiteId("all"); // Will fetch kitchens from all sites
        } else {
          setSelectedSiteId("all");
          setSelectedKitchenId("all");
        }
      } catch (error) {
        console.error("Error fetching filters:", error);
      } finally {
        setIsLoadingFilters(false);
      }
    };
    fetchFilters();
  }, [getSites, kitchenId])

  // Fetch kitchens when site changes
  useEffect(() => {
    const fetchKitchensBySelectedSite = async () => {
      if (selectedSiteId === "all") {
        // Fetch kitchens from all sites
        try {
          const allKitchens = [];
          for (const site of sites) {
            const response = await getKitchensBySite(site.site_id, {
              page: 1,
              perPage: 100,
            });
            if (response?.kitchens) {
              allKitchens.push(...response.kitchens);
            }
          }
          setKitchens(allKitchens);
        } catch (error) {
          console.error("Error fetching kitchens from all sites:", error);
        }
      } else if (selectedSiteId) {
        try {
          const response = await getKitchensBySite(selectedSiteId, {
            page: 1,
            perPage: 100,
          });
          setKitchens(response?.kitchens || []);

          // If no kitchen is selected or the selected one doesn't match the site, keep "all" selected
          if (!kitchenId && selectedKitchenId !== "all") {
            setSelectedKitchenId("all");
          }
        } catch (error) {
          console.error("Error fetching kitchens:", error);
        }
      }
    };
    fetchKitchensBySelectedSite();
  }, [selectedSiteId, getKitchensBySite, kitchenId, sites])

  // Helper function to compare versions
  // type: 'gte' for >= (greater than or equal), 'lte' for <= (less than or equal)
  const compareVersions = (version1, version2, type = "gte") => {
    if (!version1 || !version2) return false;
    const v1Parts = version1.split(".").map((n) => parseInt(n, 10) || 0);
    const v2Parts = version2.split(".").map((n) => parseInt(n, 10) || 0);
    const maxLength = Math.max(v1Parts.length, v2Parts.length);

    for (let i = 0; i < maxLength; i++) {
      const v1 = v1Parts[i] || 0;
      const v2 = v2Parts[i] || 0;
      if (v1 > v2) return type === "gte";
      if (v1 < v2) return type === "lte";
    }
    // versions are equal
    return true;
  };

  // Update total rows for single kitchen mode from API response
  useEffect(() => {
    if (selectedKitchenId && selectedKitchenId !== "all") {
      // Single kitchen uses server pagination
      const filtered = tablets || [];
      setFilteredTablets(filtered);
      console.log(pagination?.totalItems);
      
      setTotalRows(pagination?.totalItems || 0);
    }
  }, [tablets, pagination, selectedKitchenId]);

  // Update kitchen name when selectedKitchenId changes
  useEffect(() => {
    if (selectedKitchenId && selectedKitchenId !== "all") {
      const kitchenData = kitchens.find(k => k.id === selectedKitchenId);
      setKitchen(kitchenData || null);
    } else {
      setKitchen(null);
    }
  }, [selectedKitchenId, kitchens]);

  // Fetch tablets for single kitchen (server-side pagination)
  useEffect(() => {
    if (selectedKitchenId && selectedKitchenId !== "all") {
      getKitchenTablets(selectedKitchenId, {
        page: currentPage,
        perPage: rowsPerPage,
        search: searchTerm,
        appVersion: minVersion,
        versionComparisonType: versionComparisonType,
      });
    }
  }, [selectedKitchenId, currentPage, searchTerm, getKitchenTablets, rowsPerPage, minVersion, versionComparisonType]);

  // Fetch tablets for "all" mode (client-side pagination - no currentPage dependency)
  useEffect(() => {
    if (selectedKitchenId === "all") {
      if (selectedSiteId === "all") {
        // All sites: use getAllTablets endpoint
        getAllTablets({
          page: 1,
          perPage: 999,
          search: "",
          appVersion: minVersion,
          versionComparisonType: versionComparisonType,
        }).then((result) => {
          if (result?.tablets) {
            let filtered = result.tablets;
            
            if (searchTerm && searchTerm.trim()) {
              filtered = filtered.filter((tablet) =>
                tablet?.fcmToken?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                tablet?.deviceModel?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                tablet?.deviceBrand?.toLowerCase().includes(searchTerm.toLowerCase())
              );
            }
            
            if (minVersion && minVersion.trim()) {
              filtered = filtered.filter((tablet) =>
                compareVersions(tablet?.appVersion, minVersion, versionComparisonType)
              );
            }
            
            setFilteredTablets(filtered);
            setTotalRows(filtered.length);
          }
        });
      } else if (selectedSiteId && selectedSiteId !== "all") {
        // Specific site: use getTabletsBySite endpoint
        getTabletsBySite(selectedSiteId, {
          page: 1,
          perPage: 999,
          search: "",
          appVersion: minVersion,
          versionComparisonType: versionComparisonType,
        }).then((result) => {
          if (result?.tablets) {
            let filtered = result.tablets;
            
            if (searchTerm && searchTerm.trim()) {
              filtered = filtered.filter((tablet) =>
                tablet?.fcmToken?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                tablet?.deviceModel?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                tablet?.deviceBrand?.toLowerCase().includes(searchTerm.toLowerCase())
              );
            }
            
            if (minVersion && minVersion.trim()) {
              filtered = filtered.filter((tablet) =>
                compareVersions(tablet?.appVersion, minVersion, versionComparisonType)
              );
            }
            
            setFilteredTablets(filtered);
            setTotalRows(filtered.length);
          }
        });
      }
    }
  }, [selectedKitchenId, selectedSiteId, searchTerm, getAllTablets, getTabletsBySite, minVersion, versionComparisonType]);

  // Reset pagination when filters change
  useEffect(() => {
    
    setCurrentPage(1);
    console.log("this shit");
    
  }, [selectedSiteId, selectedKitchenId, minVersion, searchTerm, versionComparisonType]);

  const handleUnlinkTablet = (tablet) => {
    setSelectedTablet(tablet);
    setShowConfirmModal(true);
  };

  const confirmUnlink = async () => {
    if (selectedTablet && selectedKitchenId && selectedKitchenId !== "all") {
      const success = await unlinkTablet(selectedKitchenId, selectedTablet.fcmToken);
      if (success) {
        setShowConfirmModal(false);
        setSelectedTablet(null);
        setCurrentPage(1);
      }
    } else if (selectedKitchenId === "all") {
      alert(t("Please select a specific station to unlink a tablet"));
    }
  };

  useEffect(() => {
    console.log(currentPage);

  }, [currentPage]);
    
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    console.log(newPage);
    
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSearch = (searchValue) => {
    setSearchTerm(searchValue);

    
  };

  const handleSiteChange = (siteId) => {
    setSelectedSiteId(siteId === "all" ? "all" : Number(siteId));
    setSelectedKitchenId("all");
    setCurrentPage(1);
    console.log("executed siteod");
    
  };

  const handleKitchenChange = (kitchenId) => {
    setSelectedKitchenId(kitchenId === "all" ? "all" : Number(kitchenId));
    setCurrentPage(1);
        console.log("executed kitchenid");

  };

  const handleCopyToken = (token) => {
    navigator.clipboard.writeText(token);
    setShowCopyToast(true);
    setTimeout(() => setShowCopyToast(false), 2000);
  };

  const columns = [
   
    {
      name: t("Site"),
      selector: (row) => row?.kitchen?.site_id,
      sortable: true,
      wrap: true,
      cell: (row) => {
        const site = sites.find((s) => s.site_id === row?.kitchen?.site_id);
        return (
          <div className="d-flex flex-row justify-content-center align-items-center gap-2">
            <span>
              {row?.kitchen?.site_id && site?.name
                ? `${row.kitchen.site_id} - ${site.name}`
                : "-"}
            </span>
          </div>
        );
      },
    },
    {
      name: t("Service Station"),
      selector: (row) => row?.kitchen?.kitchen_id,
      sortable: true,
      wrap: true,
      cell: (row) => (
        <div className="d-flex flex-row justify-content-center align-items-center gap-2">
          <span>
            {row?.kitchen?.kitchen_id && row?.kitchen?.name
              ? `${row.kitchen.kitchen_id} - ${row.kitchen.name}`
              : "-"}
          </span>
        </div>
      ),
    },
     {
      name: t("FCM Token"),
      width: "220px",
      selector: (row) => row?.fcmToken,
      sortable: true,
      wrap: true,
      cell: (row) => (
        <div className="d-flex flex-row align-items-center gap-2">
          <small className="text-muted" style={{ wordBreak: "break-all" }}>
            {row?.fcmToken?.substring(0, 20)}...
          </small>
          <Button
            id={`copy-btn-${row?.kitchenTablet_id}`}
            size="sm"
            color="light"
            className="p-0"
            style={{ width: "24px", height: "24px" }}
            onClick={() => handleCopyToken(row?.fcmToken)}
            title="Copy FCM Token"
          >
            <i className="ri-file-copy-line" style={{ fontSize: "12px" }}></i>
          </Button>
          <UncontrolledTooltip
            target={`copy-btn-${row?.kitchenTablet_id}`}
            placement="top"
          >
            {t("Copy FCM Token")}
          </UncontrolledTooltip>
        </div>
      ),
    },
    {
      name: t("Device Model"),
      selector: (row) => row?.deviceModel,
      sortable: true,
      wrap: true,
      cell: (row) => (
        <div className="d-flex flex-row justify-content-center align-items-center gap-2">
          <span>{row?.deviceModel || "-"}</span>
        </div>
      ),
    },
    {
      name: t("Device Brand"),
      selector: (row) => row?.deviceBrand,
      sortable: true,
      wrap: true,
      cell: (row) => (
        <div className="d-flex flex-row justify-content-center align-items-center gap-2">
          <span>{row?.deviceBrand || "-"}</span>
        </div>
      ),
    },
    {
      name: t("App Version"),
      selector: (row) => row?.appVersion,
      sortable: true,
      wrap: true,
      cell: (row) => (
        <div className="d-flex flex-row justify-content-center align-items-center gap-2">
          <span>{row?.appVersion || "-"}</span>
        </div>
      ),
    },
    {
      name: t("OS Version"),
      selector: (row) => row?.osVersion,
      sortable: true,
      wrap: true,
      cell: (row) => (
        <div className="d-flex flex-row justify-content-center align-items-center gap-2">
          <span>{row?.osVersion || "-"}</span>
        </div>
      ),
    },
    {
      name: t("Phone OS"),
      selector: (row) => row?.phoneOS,
      sortable: true,
      wrap: true,
      cell: (row) => (
        <div className="d-flex flex-row justify-content-center align-items-center gap-2">
          <span>{row?.phoneOS || "-"}</span>
        </div>
      ),
    },
    {
      name: t("Last Updated"),
      selector: (row) => row?.updated_at,
      sortable: true,
      wrap: true,
      cell: (row) => (
        <div className="d-flex flex-row justify-content-center align-items-center gap-2">
          <span>
            {row?.updated_at
              ? moment(row?.updated_at).format("YYYY-MM-DD HH:mm")
              : "-"}
          </span>
        </div>
      ),
    },
    {
      name: t("Actions"),
      width: "120px",
      cell: (row) => (
        <div className="d-flex flex-row justify-content-center align-items-center gap-2">
          {canDeleteTablets && (
            <>
              <Button
                id={`unlink-btn-${row?.kitchenTablet_id}`}
                className="btn btn-sm btn-danger"
                onClick={() => handleUnlinkTablet(row)}
                disabled={selectedKitchenId === "all"}
                title={selectedKitchenId === "all" ? t("Select a specific station to unlink") : ""}
              >
                <i className="ri-close-line"></i>
              </Button>
              <UncontrolledTooltip
                target={`unlink-btn-${row?.kitchenTablet_id}`}
                placement="top"
              >
                {selectedKitchenId === "all"
                  ? t("Select a specific station to unlink")
                  : t("Unlink Tablet")}
              </UncontrolledTooltip>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
      <div className="container-fluid">
        {/* Breadcrumb */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="d-flex align-items-center justify-content-between">
              <h4 className="mb-0">
                {kitchen ? `${t("Station Tablets")} - ${kitchen?.name}` : t("Station Tablets")}
              </h4>
              {kitchenId && (
                <Button
                  color="secondary"
                  size="sm"
                  onClick={() => navigate("/kitchens")}
                >
                  <i className="ri-arrow-left-line"></i> {t("Back")}
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="row mb-3">
          <div className="col-12">
            <div className="d-flex flex-column flex-md-row align-items-stretch gap-2 w-100">
              {/* Site Filter */}
              {sites.length > 0 && (
                <div className="d-flex align-items-center gap-2" style={{ flex: "0 0 auto" }}>
                  <label className="mb-0 text-nowrap">{t("Site")}:</label>
                  <select
                    className="form-control"
                    value={selectedSiteId || ""}
                    onChange={(e) => handleSiteChange(e.target.value)}
                    style={{ minWidth: "200px" }}
                  >
                    <option value="all">{t("All Sites")}</option>
                    {sites.map((site) => (
                      <option key={site.site_id} value={site.site_id}>
                       {site.site_id} - {site.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Kitchen/Station Filter */}
              {kitchens.length > 0 && (
                <div className="d-flex align-items-center gap-2" style={{ flex: "0 0 auto" }}>
                  <label className="mb-0 text-nowrap">{t("Station")}:</label>
                  <select
                    className="form-control"
                    value={selectedKitchenId || ""}
                    onChange={(e) => handleKitchenChange(e.target.value)}
                    style={{ minWidth: "200px" }}
                  >
                    <option value="">{t("Select Station")}</option>
                    <option value="all">{t("All Stations")}</option>
                    {kitchens.map((k) => (
                      <option key={k.kitchen_id} value={k.kitchen_id}>
                        {k.kitchen_id} - {k.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* App Version Filter */}
              <div className="d-flex align-items-center gap-2" style={{ flex: "0 0 auto" }}>
                <label className="mb-0 text-nowrap">{t("App Version")}: </label>
                <select
                  className="form-control"
                  value={versionComparisonType}
                  onChange={(e) => {
                    setVersionComparisonType(e.target.value);
                    setCurrentPage(1);
                  }}
                  style={{ minWidth: "140px" }}
                >
                  <option value="gte">{t("Equals or Greater")}</option>
                  <option value="lte">{t("Equals or Less")}</option>
                </select>
                <input
                  type="text"
                  className="form-control"
                  placeholder="e.g., 1.0.0"
                  value={minVersion}
                  onChange={(e) => {
                    setMinVersion(e.target.value);
                    setCurrentPage(1);
                    console.log("executed version change");
                  }}
                  style={{ minWidth: "130px" }}
                />
              </div>

              {/* Search Bar */}
              <div className="flex-grow-1">
                <SearchBar
                  submitMode
                  placeholder={t("Search by FCM token, device model, or brand...")}
                  onSearch={handleSearch}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Tablets Table */}
        <div className="row">
          <div className="col-12">
            {isLoading ? (
              <Skeleton />
            ) : (
              <>
                <DataTableBase
                  columns={columns}
                  data={selectedKitchenId === "all" ? 
                    filteredTablets.slice(
                      (currentPage - 1) * rowsPerPage,
                      currentPage * rowsPerPage
                    ) :
                    filteredTablets
                  }
                  showActionColumn={false}
                />
                {filteredTablets.length > 0 && (
                  <Pagination
                    currentPage={currentPage}
                    totalRows={totalRows}
                    rowsPerPage={rowsPerPage}
                    onPageChange={handlePageChange}
                  />
                )}
              </>
            )}
          </div>
        </div>
      {/* Unlink Confirmation Modal */}
      <Modal isOpen={showConfirmModal} toggle={() => setShowConfirmModal(false)}>
        <ModalHeader toggle={() => setShowConfirmModal(false)}>
          {t("Unlink Tablet")}
        </ModalHeader>
        <ModalBody>
          {t("Are you sure you want to unlink this tablet?")} <br />
          <small className="text-muted">
            {selectedTablet?.deviceModel
              ? `${selectedTablet?.deviceBrand} ${selectedTablet?.deviceModel}`
              : selectedTablet?.fcmToken?.substring(0, 20)}
          </small>
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => setShowConfirmModal(false)}
          >
            {t("Cancel")}
          </Button>
          <Button color="danger" onClick={confirmUnlink}>
            {t("Unlink")}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Copy Toast Notification */}
      <div
        style={{
          position: "fixed",
          bottom: "20px",
          right: "20px",
          zIndex: 9999,
        }}
      >
        <Toast isOpen={showCopyToast}>
          <ToastBody className="bg-success text-white">
            <i className="ri-check-line"></i> {t("FCM Token copied to clipboard!")}
          </ToastBody>
        </Toast>
      </div>
    </div>
  );
};

export default KitchenTablets;
